import React from 'react';
import PropTypes from 'prop-types';
import {
	Title,
	Sidebar,
	SidebarWidget,
	SidebarWidgetTitle,
	SidebarWidgetInfoRow,
	Link,
	Avatar,
	Icon
} from '@upsales/components';
import { eventTypes as EventTypes, leadStatus as LeadStatus } from 'App/babel/components/LegacyReactTemplates';
import t from '../Helpers/translate';
import './AssignModalSidebar.scss';
import BemClass from '@upsales/components/Utils/bemClass';
import Client from 'App/resources/Model/Client';
import { useSoftDeployAccess } from 'App/components/hooks';
import { useDialProps } from 'App/components/hooks/appHooks';
import CreditRatingLabel from 'App/components/CreditRatingLabel';
import ClientRating from 'Components/ClientRating';
import AppService from 'App/legacy/AppService';

type Props = {
	client: Client;
};

const AssignModalSidebar = ({ client }: Props) => {
	const hasCreditRatingFromProspecting = useSoftDeployAccess('CREDIT_RATING_FROM_PROSPECTING');
	const dialProps = useDialProps();
	// The shared rule rather than a local href builder: it is what decides between the softphone,
	// the OS and a plain link, and a copy of the href half never reaches the softphone at all.
	const phone = dialProps(client.phone, { type: 'client', record: client });
	const visitAddress = client.addresses?.find(a => a.type === 'Visit');
	let addressRow = null;

	if (visitAddress) {
		const icon = visitAddress.country ? (
			<b className={`flag-icon flag-icon-${visitAddress.country.toLowerCase()}`} />
		) : (
			<Icon name="map-marker" />
		);
		addressRow = (
			<SidebarWidgetInfoRow
				key="address"
				value={
					<span>
						{icon} {visitAddress.city}
					</span>
				}
			/>
		);
	}

	const classNames = new BemClass('AssignModalSidebar');

	return (
		<Sidebar size="sm" className={classNames.b()}>
			<SidebarWidget key="title">
				<Title>
					<Link
						color="black"
						href={Tools.$state.href('account.dashboard', {
							id: client.id,
							customerId: AppService.getCustomerId()
						})}
					>
						{client.name}
					</Link>
				</Title>

				<div key="crm" className={classNames.elem('client-label').b()}>
					<LeadStatus
						theKey={client.id}
						activity={client.hasActivity}
						activityOld={client.hadActivity}
						appointment={client.hasAppointment}
						appointmentOld={client.hadAppointment}
						opportunity={client.hasOpportunity}
						opportunityOld={client.hadOpportunity}
						order={client.hasOrder}
						orderOld={client.hadOrder}
					/>
				</div>

				<div key="market" className={classNames.elem('client-label').b()}>
					<EventTypes theKey={client.id} account={client} />
				</div>

				<div key="rating" className={classNames.elem('client-label').b()}>
					{hasCreditRatingFromProspecting ? (
						<CreditRatingLabel
							prospectingCreditRating={client.prospectingCreditRating}
							prospectingNumericCreditRating={client.prospectingNumericCreditRating}
							hideIfNoRating
							company={client}
						/>
					) : (
						<ClientRating client={client} />
					)}
				</div>

				<SidebarWidgetTitle title={t('default.accountManagers')} />
				{client.users && client.users.length ? (
					client.users.map(user => (
						<SidebarWidgetInfoRow
							key={'user-' + user.id}
							placeholder={t('default.noAccountManager')}
							value={
								<div>
									<Avatar
										size="sm"
										initials={user.name}
										email={user.email}
										style={{ verticalAlign: 'middle' }}
									/>{' '}
									{user.name}
								</div>
							}
						/>
					))
				) : (
					<SidebarWidgetInfoRow placeholder={t('default.noAccountManager')} />
				)}
			</SidebarWidget>

			<SidebarWidget key="client-info">
				<SidebarWidgetTitle title={t('admin.clientInfo')} />
				{addressRow}
				<SidebarWidgetInfoRow
					key="phone"
					color="bright-blue"
					icon="phone"
					placeholder={t('default.noPhone')}
					value={
						phone ? (
							<Link href={phone.href} target={phone.target} onClick={phone.onClick}>
								{client.phone}
							</Link>
						) : null
					}
				/>
				<SidebarWidgetInfoRow
					key="website"
					color="bright-blue"
					icon="globe"
					placeholder={t('default.noWebsite')}
					value={
						client.webpage ? (
							<Link href={client.webpage} target="_blank">
								{client.webpage}
							</Link>
						) : null
					}
				/>
			</SidebarWidget>
		</Sidebar>
	);
};

AssignModalSidebar.propTypes = {
	client: PropTypes.object.isRequired
};

export default AssignModalSidebar;
